import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import SearchIcon from '@mui/icons-material/Search'
import RefreshIcon from '@mui/icons-material/Refresh'
import LockResetIcon from '@mui/icons-material/LockReset'
import { useSnackbar } from 'notistack'
import { PageHeader } from '@/components/common/PageHeader'
import { authStore } from '@/store/authStore'
import { usuarioService } from '@/services/usuarioService'
import { tenantService } from '@/services/tenantService'
import { CANAIS_COMERCIAIS, CANAL_LABEL, CanalComercial } from '@/types/statusReserva'
import { mensagemErroDe } from '@/components/pipeline/erros'
import { TenantAdmin } from '@/types/tenant'
import {
  PAPEL_GESTOR_EQUIPE,
  UsuarioAdmin,
  UsuarioCreateRequest,
  UsuarioRole,
  UsuarioUpdateRequest,
} from '@/types/usuario'

const ROLES: {
  value: UsuarioRole
  label: string
  color: 'error' | 'warning' | 'info' | 'success'
  descricao: string
}[] = [
  { value: 'ADMIN', label: 'Administrador', color: 'error', descricao: 'Acesso total ao sistema' },
  { value: 'GERENTE', label: 'Gerente', color: 'warning', descricao: 'Acesso à equipe e relatórios' },
  { value: 'CONSULTOR', label: 'Consultor', color: 'success', descricao: 'Operacional — gerencia agenda própria' },
  { value: 'OPERADOR', label: 'Operador', color: 'info', descricao: 'Acesso operacional' },
]

// Detecta o papel principal do usuário. Ordem de precedência: ADMIN > GERENTE > CONSULTOR > OPERADOR.
const PRECEDENCIA: UsuarioRole[] = ['ADMIN', 'GERENTE', 'CONSULTOR', 'OPERADOR']
const rolePrincipal = (roles: string[]): UsuarioRole | '' => {
  const upper = new Set(roles.map((r) => r.toUpperCase()))
  for (const papel of PRECEDENCIA) {
    if (upper.has(papel)) return papel
  }
  return ''
}

const ehGestorEquipe = (roles: string[] | undefined): boolean =>
  (roles ?? []).some((r) => r.toUpperCase() === PAPEL_GESTOR_EQUIPE)

const canalValido = (c: string): c is CanalComercial =>
  (CANAIS_COMERCIAIS as string[]).includes(c)

const PapelChip: FC<{ roles: string[] | undefined }> = ({ roles }) => {
  const papel = rolePrincipal(roles ?? [])
  if (!papel) return <>—</>
  const def = ROLES.find((r) => r.value === papel)
  return (
    <Stack direction="row" spacing={0.5}>
      <Chip label={def?.label ?? papel} size="small" color={def?.color} variant="outlined" />
      {papel === 'GERENTE' && ehGestorEquipe(roles) && (
        <Chip label="Gestor de equipe" size="small" variant="outlined" />
      )}
    </Stack>
  )
}

const UsuariosListPage: FC = () => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const usuarioAtual = authStore((s) => s.usuario)
  const isAdmin = useMemo(
    () => !!usuarioAtual?.permissoes?.some((p) => p.toUpperCase() === 'ADMIN'),
    [usuarioAtual]
  )

  // Proteção de rota cliente-side (o backend já exige ROLE_ADMIN).
  useEffect(() => {
    if (usuarioAtual && !isAdmin) {
      enqueueSnackbar('Acesso restrito a administradores', { variant: 'warning' })
      navigate('/dashboard')
    }
  }, [usuarioAtual, isAdmin, navigate, enqueueSnackbar])

  const [usuarios, setUsuarios] = useState<UsuarioAdmin[]>([])
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editando, setEditando] = useState<UsuarioAdmin | null>(null)
  const [salvando, setSalvando] = useState(false)

  // form
  const [email, setEmail] = useState('')
  const [nome, setNome] = useState('')
  const [senha, setSenha] = useState('')
  const [role, setRole] = useState<UsuarioRole>('OPERADOR')
  const [tipoCanal, setTipoCanal] = useState('')
  const [cpf, setCpf] = useState('')
  const [cnpj, setCnpj] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [tenantId, setTenantId] = useState('')
  // AN-8 §4: papel complementar GESTOR_EQUIPE — só faz sentido (e só é aceito) com GERENTE.
  const [gestorEquipe, setGestorEquipe] = useState(false)
  const [tenants, setTenants] = useState<TenantAdmin[]>([])

  const carregar = useCallback(async () => {
    if (!isAdmin) return
    setLoading(true)
    setErro(null)
    try {
      const lista = await usuarioService.listar({ search: search.trim() || undefined })
      setUsuarios(lista)
    } catch (e) {
      setErro(mensagemErroDe(e, 'Falha ao listar usuários'))
      setUsuarios([])
    } finally {
      setLoading(false)
    }
  }, [isAdmin, search])

  useEffect(() => {
    carregar()
  }, [carregar])

  // Lista de tenants para o selector (obrigatório ao criar CONSULTOR).
  useEffect(() => {
    if (!isAdmin) return
    tenantService.listar().then(setTenants).catch(() => setTenants([]))
  }, [isAdmin])

  const abrirNovo = () => {
    setEditando(null)
    setEmail('')
    setNome('')
    setSenha('')
    setRole('OPERADOR')
    setTipoCanal('')
    setCpf('')
    setCnpj('')
    setEnabled(true)
    setTenantId('')
    setGestorEquipe(false)
    setDialogOpen(true)
  }

  const abrirEdicao = async (u: UsuarioAdmin) => {
    setEditando(u)
    setEmail(u.email ?? '')
    setNome(u.nome ?? '')
    setSenha('')
    setTipoCanal(u.tipoCanal ?? '')
    setCpf(u.cpf ?? '')
    setCnpj(u.cnpj ?? '')
    setEnabled(u.enabled ?? true)
    setTenantId(u.tenantId ?? '')

    // Roles já vêm no listar (coluna PAPEL); tenantId segue só no detalhe, porque a
    // listagem do Keycloak é a representação resumida, sem attributes.
    setRole(rolePrincipal(u.roles ?? []) || 'OPERADOR')
    setGestorEquipe(ehGestorEquipe(u.roles))
    try {
      const detalhe = await usuarioService.obter(u.id)
      setRole(rolePrincipal(detalhe.roles) || 'OPERADOR')
      setGestorEquipe(ehGestorEquipe(detalhe.roles))
      setTenantId(detalhe.tenantId ?? '')
    } catch {
      // mantém o papel visto na listagem
    }
    setDialogOpen(true)
  }

  const fechar = () => setDialogOpen(false)

  const podeSalvar = useMemo(() => {
    if (!nome.trim()) return false
    if (!editando && (!email.trim() || !senha || senha.length < 8)) return false
    if (editando && senha && senha.length < 8) return false
    if (role === 'CONSULTOR' && !tenantId) return false // consultor exige tenant
    return true
  }, [nome, email, senha, editando, role, tenantId])

  const salvar = async () => {
    if (!podeSalvar) return
    setSalvando(true)
    try {
      if (editando) {
        const payload: UsuarioUpdateRequest = {
          nome: nome.trim(),
          tipoCanal: canalValido(tipoCanal) ? tipoCanal : null,
          cpf: cpf.trim() || null,
          cnpj: cnpj.trim() || null,
          tenantId: role === 'CONSULTOR' ? (tenantId || null) : null,
          enabled,
          novaSenha: senha ? senha : null,
          role,
          // Fora de GERENTE manda false: o backend removeria de qualquer jeito, e assim o
          // checkbox escondido nunca dispara 422 GESTOR_EQUIPE_SO_GERENTE.
          gestorEquipe: role === 'GERENTE' ? gestorEquipe : false,
        }
        await usuarioService.atualizar(editando.id, payload)
        enqueueSnackbar('Usuário atualizado', { variant: 'success' })
      } else {
        const payload: UsuarioCreateRequest = {
          email: email.trim(),
          nome: nome.trim(),
          senha,
          tipoCanal: canalValido(tipoCanal) ? tipoCanal : null,
          cpf: cpf.trim() || null,
          cnpj: cnpj.trim() || null,
          tenantId: role === 'CONSULTOR' ? (tenantId || null) : null,
          role,
          gestorEquipe: role === 'GERENTE' ? gestorEquipe : false,
        }
        await usuarioService.criar(payload)
        enqueueSnackbar('Usuário criado', { variant: 'success' })
      }
      setDialogOpen(false)
      await carregar()
    } catch (e) {
      enqueueSnackbar(mensagemErroDe(e, 'Falha ao salvar usuário'), {
        variant: 'error',
      })
    } finally {
      setSalvando(false)
    }
  }

  const resetar2fa = async (u: UsuarioAdmin) => {
    if (
      !window.confirm(
        `Resetar o 2FA de "${u.nome || u.email}"? Ele precisará cadastrar o autenticador novamente no próximo login.`,
      )
    )
      return
    try {
      await usuarioService.resetarTwoFactor(u.id)
      enqueueSnackbar('2FA resetado — usuário reconfigura no próximo login', { variant: 'success' })
    } catch (e) {
      enqueueSnackbar(mensagemErroDe(e, 'Falha ao resetar 2FA'), {
        variant: 'error',
      })
    }
  }

  const deletar = async (u: UsuarioAdmin) => {
    if (u.id === usuarioAtual?.id) {
      enqueueSnackbar('Você não pode remover seu próprio usuário', { variant: 'warning' })
      return
    }
    if (!window.confirm(`Remover o usuário "${u.nome || u.email}"?`)) return
    try {
      await usuarioService.deletar(u.id)
      enqueueSnackbar('Usuário removido', { variant: 'success' })
      await carregar()
    } catch (e) {
      enqueueSnackbar(mensagemErroDe(e, 'Falha ao remover usuário'), {
        variant: 'error',
      })
    }
  }

  if (!isAdmin) return null

  return (
    <Box>
      <PageHeader
        title="Usuários"
        subtitle="Gerencie o acesso da equipe ao portal"
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNovo}>
            Novo usuário
          </Button>
        }
      />

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Buscar por nome, e-mail…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') carregar()
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, maxWidth: 420 }}
          />
          <Button variant="outlined" onClick={carregar}>
            Buscar
          </Button>
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={carregar} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Paper>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>NOME</strong></TableCell>
                <TableCell><strong>E-MAIL</strong></TableCell>
                <TableCell><strong>PAPEL</strong></TableCell>
                <TableCell><strong>CANAL</strong></TableCell>
                <TableCell><strong>STATUS</strong></TableCell>
                <TableCell align="right"><strong>AÇÕES</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : usuarios.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhum usuário encontrado.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                usuarios.map((u) => (
                  <TableRow
                    key={u.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => abrirEdicao(u)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {u.nome || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      <PapelChip roles={u.roles} />
                    </TableCell>
                    <TableCell>
                      {/* Lista fechada (6.7): rótulo do canal; código legado aparece cru. */}
                      {u.tipoCanal
                        ? (CANAL_LABEL[u.tipoCanal as CanalComercial] ?? u.tipoCanal)
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={u.enabled ? 'Ativo' : 'Inativo'}
                        size="small"
                        color={u.enabled ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => abrirEdicao(u)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Resetar 2FA">
                        <IconButton size="small" onClick={() => resetar2fa(u)}>
                          <LockResetIcon fontSize="small" color="warning" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remover">
                        <IconButton size="small" onClick={() => deletar(u)}>
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ── MODAL: Criar / Editar ── */}
      <Dialog open={dialogOpen} onClose={fechar} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0, fontWeight: 700 }}>
          {editando ? 'Editar usuário' : 'Novo usuário'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nome completo"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              fullWidth
              required
              autoFocus
            />

            <TextField
              label="E-mail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              required
              disabled={!!editando}
              helperText={
                editando
                  ? 'E-mail não pode ser alterado após o cadastro'
                  : 'Será usado como login'
              }
            />

            <TextField
              label={editando ? 'Nova senha (opcional)' : 'Senha'}
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              fullWidth
              required={!editando}
              helperText="Mínimo de 8 caracteres"
            />

            <FormControl fullWidth>
              <InputLabel>Papel</InputLabel>
              <Select
                value={role}
                label="Papel"
                onChange={(e) => setRole(e.target.value as UsuarioRole)}
              >
                {ROLES.map((r) => (
                  <MenuItem key={r.value} value={r.value}>
                    <Chip label={r.label} size="small" color={r.color} sx={{ mr: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      {r.descricao}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {role === 'GERENTE' && (
              <FormControl>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={gestorEquipe}
                      onChange={(_, v) => setGestorEquipe(v)}
                    />
                  }
                  label="Gestor de equipe (campanhas)"
                />
                <FormHelperText sx={{ mt: 0 }}>
                  Gerencia inscrições, metas individuais e o ranking da própria equipe nas campanhas.
                </FormHelperText>
              </FormControl>
            )}

            {role === 'CONSULTOR' && (
              <FormControl fullWidth required error={!tenantId}>
                <InputLabel>Tenant (empresa)</InputLabel>
                <Select
                  value={tenantId}
                  label="Tenant (empresa)"
                  onChange={(e) => setTenantId(e.target.value)}
                >
                  {tenants
                    .filter((t) => t.ativo || t.id === tenantId)
                    .map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.nome} <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{t.cnpj}</Typography>
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}

            {/* Lista fechada (6.7): o backend recusa qualquer outro código (CANAL_INVALIDO).
                Valor gravado fora da lista (legado, digitação antiga) aparece para o admin
                ver, mas não é reenviado — salvar sem trocar mantém o atributo como está. */}
            <FormControl fullWidth>
              <InputLabel id="usuario-canal-label">Canal comercial</InputLabel>
              <Select
                labelId="usuario-canal-label"
                value={tipoCanal}
                label="Canal comercial"
                onChange={(e) => setTipoCanal(e.target.value)}
              >
                <MenuItem value="">
                  <Typography variant="body2" color="text.secondary">Sem canal</Typography>
                </MenuItem>
                {CANAIS_COMERCIAIS.map((c) => (
                  <MenuItem key={c} value={c}>
                    {CANAL_LABEL[c]}
                  </MenuItem>
                ))}
                {tipoCanal && !canalValido(tipoCanal) && (
                  <MenuItem value={tipoCanal} disabled>
                    {tipoCanal} (fora da lista — escolha um canal)
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="CPF"
                value={cpf}
                onChange={(e) => setCpf(e.target.value)}
                fullWidth
              />
              <TextField
                label="CNPJ"
                value={cnpj}
                onChange={(e) => setCnpj(e.target.value)}
                fullWidth
              />
            </Stack>

            {editando && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Switch
                  checked={enabled}
                  onChange={(_, v) => setEnabled(v)}
                  disabled={editando.id === usuarioAtual?.id}
                />
                <Typography variant="body2">
                  {enabled ? 'Usuário ativo' : 'Usuário inativo (login bloqueado)'}
                </Typography>
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={fechar} disabled={salvando}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={salvar} disabled={!podeSalvar || salvando}>
            {salvando ? 'Salvando…' : editando ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default UsuariosListPage
